import React, {useEffect} from "react";
import './brand-colors.scss';
import {useDispatch, useSelector} from "react-redux";
import {getContentForBrandContentType} from "../../../mystuff-brands/brands-thunk";
import {
    areBrandsLoading,
    areThereNoBrandsToShow,
    BRAND_ASSET_NAME,
    BRAND_ASSETS,
    BRANDS_LOADING_STATES,
    fetchBrandsResourceIfNotLoaded,
    isBrandContentLoadingForContentType,
    isThereNoBrandContent
} from "@Libraries/brands-library";
import {BrandSectionLoader} from "../../../mystuff-brands/components/brand-section-loader";
import {DottedButton} from "../dotted-button";
import {executeThunk} from "@Utils/thunk.util";
import {ColorSwatch} from "../../../color-picker/components/color-swatch";
import PropTypes from "prop-types";
import {useActiveBrandId} from "@Hooks/brands/useActiveBrandId";

/**
 * The main component displaying the brand colors in Editor color panel
 * @author Afifa Saleem <afifa@250mils.com>
 */
const BrandColors = ({onColorSelect = $.noop, onAddBrandAsset}) => {
    const brands = useSelector(({brands}) => brands.brands),
        activeBrandId = useActiveBrandId(),
        brandsLoadingState = useSelector(({brands}) => brands.brandsLoadingState),
        brandContent = useSelector(({brands}) => brands.brandContent),
        brandColors = useSelector(({brands}) => brands.colors),
        dispatchReduxThunk = useDispatch(),
        fetchBrandColors = () => executeThunk(() => dispatchReduxThunk(getContentForBrandContentType(
            {brandId: activeBrandId, brandContentType: BRAND_ASSETS.BRAND_COLORS}
        )));


    const getBrandColorsLoadingState = () => {
        return areThereNoBrandsToShow(brands, brandsLoadingState) ? BRANDS_LOADING_STATES.LOADED : brandContent[activeBrandId].colors.loaded;
    }

    useEffect(() => {
        fetchBrandsResourceIfNotLoaded(getBrandColorsLoadingState(), fetchBrandColors);
    }, [activeBrandId]);

    /**
     * handler for when color is clicked
     * @param color
     */
    const onAddColor = (color) => {
        onColorSelect(color);
    }

    /**
     * @returns {JSX.Element}
     */
    const getBrandColorContent = () => {
        let swatches = brandContent[activeBrandId].colors.ids.map(id => {
            return <ColorSwatch
                key={brandColors[id].idbrandColor}
                color={brandColors[id].hexCode}
                onClick={onAddColor}
            />
        });
        return (
            <div className={'color-swatch-container'}>
                <ul className={'color-swatches'}>
                    {swatches}
                </ul>
            </div>
        );
    }

    /**
     * @returns {JSX.Element}
     */
    const getBrandContent = () => {
        if (areBrandsLoading(brandsLoadingState) || isBrandContentLoadingForContentType(activeBrandId, brandContent, BRAND_ASSET_NAME.BRAND_COLORS)) {
            return <BrandSectionLoader/>;
        }

        if (areThereNoBrandsToShow(brands, brandsLoadingState)) {
            return <DottedButton buttonText={i18next.t('pmwjs_add_a_brand')} buttonContext={BRAND_ASSETS.BRAND_COLORS} buttonIcon={'icon-plus'} activeBrandId={activeBrandId} onClick={onAddBrandAsset}/>;
        }

        if (isThereNoBrandContent(activeBrandId, brandContent, BRAND_ASSET_NAME.BRAND_COLORS)) {
            return <DottedButton buttonText={i18next.t('pmwjs_add_a_color')} buttonIcon={'icon-plus'} buttonContext={BRAND_ASSETS.BRAND_COLORS} activeBrandId={activeBrandId} onClick={onAddBrandAsset}/>;
        }

        return getBrandColorContent();
    }

    return (
        <div className={'brand-content'}>
            {getBrandContent()}
        </div>
    )
}

BrandColors.propTypes = {
    /**
     * a handler for when a brand color is selected
     */
    onColorSelect: PropTypes.func,
    /**
     * a handler for add brand asset button
     */
    onAddBrandAsset: PropTypes.func.isRequired
};


export default BrandColors;